// Card #1795 / #1851 — the Message length-tier cutoffs, split out of
// MessageLayout.jsx into their own CSS-free module so Cypress specs can
// import the real constants (not a duplicated literal) without dragging
// MessageLayout.css through the Cypress webpack preprocessor, which has no
// CSS loader configured and fails the whole spec file on that import
// (confirmed: "Module parse failed: Unexpected token" pointing at the .css
// file, the moment a spec imported MessageLayout.jsx directly).
//
// Boundaries are fractions of MESSAGE_TEXT_MAX_LENGTH so the tiers stay
// correct if that cap is ever retuned, rather than duplicating a second
// hardcoded 240 here.
//
// Card #1851 (filed off #1795's own second judge denial): measured,
// composition-dependent overflow at the LITERAL tier ceilings — ordinary
// (not adversarial) messages at 52-60 chars overflowed the short tier's
// 9vw box, and ordinary messages at 116-138 chars overflowed the medium
// tier's 6.5vw box (see MessageLayout.css's tier comments for the measured
// numbers). Both fractions are lowered here so those two overflowing bands
// drop one tier entirely (52-60 -> medium, 116-138 -> long), verified by
// direct measurement (real Cypress runs, scrollHeight vs clientHeight
// against a running preview) in scoreboard-message.cy.js, not calculated.
//
// Neither cutoff removes the underlying composition-dependent risk (a
// static length tier can't: word-wrap breakpoints, not just char count,
// decide how many lines a message needs). Measured directly while probing
// this fix: at the medium tier, a 93-char message fits (624<=624px) but a
// different 95-char message overflows (780>702px) — two chars apart. 93
// is the new medium ceiling because it is the longest length actually
// measured clean; nothing above it is claimed safe. This residual risk is
// real and undocumented in AC6 (see the card's own N/A note).
export const SHORT_TIER_LENGTH_FRACTION = 0.2; // 48 chars: short-tier messages at 44 and 48 chars both measured clean (648<=648px); comfortably below the measured 52-60 char overflow band
export const MEDIUM_TIER_LENGTH_FRACTION = 0.3875; // 93 chars: the longest medium-tier length actually measured clean (624<=624px); a 95-char message measured to overflow (780>702px)

export function lengthTierClassName(length, maxLength) {
  if (length <= maxLength * SHORT_TIER_LENGTH_FRACTION) {
    return "scoreboard-message__text--short";
  }
  if (length <= maxLength * MEDIUM_TIER_LENGTH_FRACTION) {
    return "scoreboard-message__text--medium";
  }
  return "scoreboard-message__text--long";
}

// Card #2502 -- per-shape tiers for the 2:1 and 4:3 canvases. The 16:9 tiers
// above are UNCHANGED (card #1851 owns their known boundary hole). Each entry
// is the last length that still uses that tier; the final tier takes the rest
// up to MESSAGE_TEXT_MAX_LENGTH. Cutoffs are the 10 percent-trimmed lengths up
// to which every measured composition fit the text box in the WIDEST of the
// eight theme fonts (Montserrat), measured in real Chrome 153 at each shape's
// box (1690x702 at 2:1, 1286.4x702 at 4:3) over 80 compositions per length at
// every length from 5 to 240. The per-font numbers and the font size of each
// tier are in MessageLayout.css next to the rules that use them. 2:1 and 4:3
// share the 702px box height and the 240-char cap, so a cap change
// invalidates these: messageLengthTiers.test.js pins the cap.
export const MESSAGE_SHAPE_TIERS = {
  "2:1": { suffix: "s2x1", through: { short: 32, medium: 54, long: 187 } },
  "4:3": { suffix: "s4x3", through: { short: 24, medium: 44, long: 153 } },
};

const TIER_ORDER = ["short", "medium", "long"];

// The tier name ("short" | "medium" | "long" | "floor") a message of `length`
// uses at `shape`.
export function shapeTierName(shape, length) {
  const { through } = MESSAGE_SHAPE_TIERS[shape];
  return TIER_ORDER.find((tier) => length <= through[tier]) ?? "floor";
}

// Both shapes' tier classes, space separated. MessageLayout does not know the
// canvas shape (it is a CSS container query's job), so it names a tier for
// each and MessageLayout.css switches on only the one inside its @container.
export function shapeTierClassNames(length) {
  return Object.entries(MESSAGE_SHAPE_TIERS)
    .map(([shape, { suffix }]) => `scoreboard-message__text--${suffix}-${shapeTierName(shape, length)}`)
    .join(" ");
}
